import React, { useCallback } from 'react';
import { Box, Button, TextField, Checkbox, FormControlLabel, LinearProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';
import Firebase from 'firebase/app';
import { useAuthentication } from '../../../hooks';
import { makeStyles } from '@material-ui/styles';
import { Profile } from '../../../store/reducers';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import { firebaseConfig } from '../../../rrf.config';

const useStyle = makeStyles({
  forgotPassword: {
    textTransform: 'none',
  },
});

type Props = {
  showRegister?: boolean;
  parent?: Profile | null;
  width?: string | number;
  onLoginCallback?: () => void;
  onRegisterCallback?: () => void;
  users?: Profile[];
};

const LoginBox: React.FC<Props> = ({ showRegister, parent, onRegisterCallback, users }) => {
  const { error } = useAuthentication();
  const [feError, setFeError] = useState('');
  const [confirmReactivation, setConfirmReactivation] = useState<null | string>(null);
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [terms, setTerms] = useState<boolean>(false);
  const [resetPassword, setResetPassword] = useState<boolean>(false);
  const { forgotPassword } = useStyle();
  const firebase = useFirebase();
  const firestore = useFirestore();

  const onLogin = useCallback(() => {
    if (email && password) {
      firebase.login({ email, password });
    }
  }, [email, firebase, password]);

  const onRegister = useCallback(() => {
    setFeError('');
    if (email && password) {
      setLoading(true);
      const secondaryApp = Firebase.initializeApp(firebaseConfig, 'Secondary');

      if (users?.find((user) => user.email === email)) {
        if (users?.find((user) => user.email === email)?.inactive) {
          setFeError('Un utente con questa email risulta già registrato ma precedentemente disattivato.');
          setConfirmReactivation(users?.find((user) => user.email === email)?.id ?? null);
        } else {
          setFeError('Un utente con questa email risulta già registrato');
        }
        return;
      }

      secondaryApp
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(function (firebaseUser) {
          if (secondaryApp.auth().currentUser?.uid) {
            const user: Partial<Profile> = {
              email,
              name,
            };
            if (parent) user.parent = parent.id;
            firestore.collection('users_meta').doc(secondaryApp.auth().currentUser?.uid).set(user);
            secondaryApp.auth().signOut();
          }
          fetch(
            `https://www.nhco-nutrition.it/?rest_route=/emailapi/v1/submit-form&email=${email}&name=${name}&password=${password}`
          );
        })
        .catch((error) => console.error(error))
        .finally(() => {
          secondaryApp.delete();
          setLoading(false);
          setName('');
          setEmail('');
          setPassword('');
          onRegisterCallback && onRegisterCallback();
        });
    }
  }, [email, password, users, name, parent, firestore, onRegisterCallback]);

  const toggleResetPassword = useCallback(() => setResetPassword(!resetPassword), [resetPassword]);

  const onReactivateAccount = useCallback(async () => {
    if (confirmReactivation) {
      await firestore.collection('users_meta').doc(confirmReactivation).update({ inactive: false });
      setLoading(false);
      onRegisterCallback && onRegisterCallback();
    }
  }, [confirmReactivation, firestore, onRegisterCallback]);

  const handleResetPassword = useCallback(async () => {
    setLoading(true);
    await firebase.resetPassword(email);
    setLoading(false);
    setEmail('');
  }, [email, firebase]);

  const renderCta = useCallback(() => {
    if (showRegister) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={onRegister}
          fullWidth
          disabled={!email || !name || !password || loading}
        >
          Registra nuova farmacia
        </Button>
      );
    }

    if (resetPassword) {
      return (
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Button
            disabled={!email}
            disableElevation
            variant="contained"
            color="primary"
            onClick={handleResetPassword}
            fullWidth
          >
            Invia email per recupero password
          </Button>
          <Button variant="text" color="primary" onClick={toggleResetPassword} className={forgotPassword}>
            Torna a Login
          </Button>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Button
          disabled={!email || !password || !terms}
          disableElevation
          variant="contained"
          color="primary"
          onClick={onLogin}
          fullWidth
        >
          Login
        </Button>
        <Button variant="text" color="primary" onClick={toggleResetPassword} className={forgotPassword}>
          Password dimenticata
        </Button>
      </Box>
    );
  }, [
    showRegister,
    resetPassword,
    loading,
    email,
    password,
    terms,
    onLogin,
    toggleResetPassword,
    forgotPassword,
    onRegister,
    name,
    handleResetPassword,
  ]);

  return (
    <Box maxWidth="100%" margin="auto">
      <Box>
        <Box padding={2}>
          {loading && <LinearProgress />}
          {showRegister && (
            <>
              <TextField
                name="name"
                label="Nome farmacia"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="filled"
                size="small"
              />
              <div style={{ margin: '10px 0' }} />
              {parent && (
                <>
                  <TextField
                    name="name"
                    label="Associato a "
                    fullWidth
                    value={parent.name}
                    disabled
                    variant="filled"
                    size="small"
                  />
                  <div style={{ margin: '10px 0' }} />
                </>
              )}
            </>
          )}
          <TextField
            name="email"
            label="Email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="filled"
            size="small"
          />
          <div style={{ margin: '10px 0' }} />
          {!resetPassword && (
            <TextField
              name="password"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="filled"
              size="small"
            />
          )}
          {!showRegister && !resetPassword && (
            <>
              <div style={{ margin: '10px 0' }} />
              <FormControlLabel
                control={<Checkbox color="primary" value={terms} onChange={(_, v) => setTerms(v)} />}
                label={
                  <span>
                    Dichiaro di avere letto e accettato i{' '}
                    <a href="https://nhco-nutrition.it/termini-e-condizioni/" target="_blank" rel="noreferrer">
                      termini e le condizioni di utilizzo
                    </a>{' '}
                    e la nostra{' '}
                    <a href="https://nhco-nutrition.it/cookie-policy/" target="_blank" rel="noreferrer">
                      politica sulla privacy
                    </a>
                  </span>
                }
              />
            </>
          )}
          <div style={{ margin: '10px 0' }} />
          {renderCta()}
          {(error || error?.code || feError) && (
            <Alert style={{ margin: '20px 0' }} severity="error">
              {feError ?? error ?? error?.code}
            </Alert>
          )}
          {confirmReactivation && (
            <Button variant="contained" color="primary" onClick={onReactivateAccount} fullWidth>
              Conferma riattivazione
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

LoginBox.displayName = 'LoginBox';

export default React.memo(LoginBox);
